
import {readOptOut} from 'utils/privacy';

import {globToRegExp, uuid} from './util';

// Never recorded, whatever the project config says: reports and results
// (PHI), account pages and payment. Events still flow on these pages.
export const BUILTIN_EXCLUDED = [
	'/report-dexa*',
	'/shared-dexa*',
	'/iframe-dexa*',
	'/appview-dexa*',
	'/reports*',
	'/report-builder*',
	'/takeaways*',
	'/assistant*',
	'/account-*',
	'/dashboard*',
	'/transactions*',
	'/booked-appointments*',
	'/referrals*',
	'/gifting-center*',
	'/support*',
	'/change-password*',
	'/reset-password*',
	'/checkout-setup*',
	'/checkout-payment*',
	'/booking-success*',
];

const CHUNK_MS = 10000;
const CHUNK_BYTES = 256 * 1024;
// Only one tab per session records, or two DOM streams would interleave
const LOCK_KEY = 'obs-rec-lock';
const LOCK_STALE_MS = 15000;
const SEQ_KEY = 'obs-rec-seq';
// Recorded just before pausing for an excluded page, so the dashboard player
// can show "recording paused" instead of a frozen previous page. Recording
// resumes with a full snapshot. Must match observer's ReplayPlayer.
const PAUSED_TAG = 'obs-paused';

type RecordEvent = {timestamp: number};

export type ReplayTarget = {
	endpoint: string
	key: string
	sessionId: string
	anonId: string
};

const storage = (): Storage | null => {
	try {
		return window.localStorage;
	} catch {
		return null;
	}
};

const gzip = async (text: string): Promise<Blob> => {
	const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
	return new Response(stream).blob();
};

export const replaySupported = (): boolean =>
	typeof CompressionStream !== 'undefined' && typeof Blob.prototype.stream === 'function';

export class Replay {
	private stopFn: (() => void) | null = null;
	private addCustomEvent: ((tag: string, payload: unknown) => void) | null = null;
	private buffer: RecordEvent[] = [];
	private bytes = 0;
	private timer: ReturnType<typeof setInterval> | null = null;
	private tabId = uuid();
	private excluded: RegExp[] = BUILTIN_EXCLUDED.map(globToRegExp);
	private target: ReplayTarget | null = null;
	private starting = false;
	// Where the visitor is now. Updated by beforeRoute as well as onPage, so a
	// start that finishes after a navigation sees the destination.
	private latestPath = '';

	setExcluded(extra: string[]) {
		this.excluded = [...BUILTIN_EXCLUDED, ...extra].map(globToRegExp);
	}

	isExcluded(path: string): boolean {
		return this.excluded.some((re) => re.test(path));
	}

	get recording(): boolean {
		return this.stopFn !== null;
	}

	// Gatsby onPreRouteUpdate: runs BEFORE the next page renders. Stopping
	// here (not in onRouteUpdate) is what keeps an excluded page's DOM out of
	// the recording entirely.
	beforeRoute(path: string) {
		this.latestPath = path;
		if (this.isExcluded(path)) {
			if (this.recording) {
				this.addCustomEvent?.(PAUSED_TAG, {reason: 'private-page'});
			}
			this.stop();
		}
	}

	// Start or pause recording for the page now showing
	async onPage(path: string, target: ReplayTarget) {
		if (this.target && this.target.sessionId !== target.sessionId) {
			this.stop();
		}
		this.target = target;
		this.latestPath = path;
		if (this.isExcluded(path)) {
			this.stop();
			return;
		}
		if (this.recording || this.starting || !this.acquireLock()) {
			return;
		}
		this.starting = true;
		try {
			// Lazy-loaded: visitors outside the sample never download rrweb
			const {record} = await import('@rrweb/record');
			// The import is async: the visitor may have moved to an excluded
			// page, switched session or opted out meanwhile. record() would
			// snapshot whatever is on screen now, so re-check everything.
			if (
				this.recording ||
				readOptOut() ||
				this.isExcluded(this.latestPath) ||
				this.target?.sessionId !== target.sessionId
			) {
				if (!this.recording) {
					this.releaseLock();
				}
				return;
			}
			this.addCustomEvent = (tag, payload) => record.addCustomEvent(tag, payload);
			const stop = record({
				emit: (event: RecordEvent) => this.push(event),
				maskAllInputs: true,
				blockClass: 'obs-block',
				blockSelector: '[data-obs-block]',
				maskTextClass: 'obs-mask',
				recordCanvas: false,
				collectFonts: false,
				// A periodic full snapshot keeps long replays seekable
				checkoutEveryNms: 5 * 60 * 1000,
				sampling: {mousemove: 50, scroll: 150, input: 'last'},
				slimDOMOptions: 'all',
			});
			this.stopFn = stop || null;
			this.timer = setInterval(() => {
				this.heartbeat();
				void this.flush();
			}, CHUNK_MS);
		} catch {
			this.releaseLock();
		} finally {
			this.starting = false;
		}
	}

	stop() {
		if (this.stopFn) {
			this.stopFn();
			this.stopFn = null;
		}
		if (this.timer) {
			clearInterval(this.timer);
			this.timer = null;
		}
		void this.flush();
		this.releaseLock();
	}

	// Drop buffered data without sending (opt-out)
	discard() {
		this.buffer = [];
		this.bytes = 0;
		this.stop();
	}

	private push(event: RecordEvent) {
		this.buffer.push(event);
		// Rough size estimate; exact JSON length is checked at flush
		this.bytes += 200;
		if (this.bytes >= CHUNK_BYTES) {
			void this.flush();
		}
	}

	// keepalive lets the request outlive the page; browsers cap it at 64KB
	async flush(onHide = false): Promise<void> {
		const target = this.target;
		if (!target || !this.buffer.length) {
			return;
		}
		if (readOptOut()) {
			this.buffer = [];
			this.bytes = 0;
			return;
		}
		const events = this.buffer;
		this.buffer = [];
		this.bytes = 0;
		const seq = this.nextSeq(target.sessionId);
		const params = new URLSearchParams({
			k: target.key,
			s: target.sessionId,
			aid: target.anonId,
			seq: String(seq),
			kind: 'rrweb',
			start: String(events[0].timestamp),
			end: String(events[events.length - 1].timestamp),
			sent_at: String(Date.now()),
		});
		try {
			const body = await gzip(JSON.stringify(events));
			await fetch(`${target.endpoint}/r?${params}`, {
				method: 'POST',
				headers: {'Content-Type': 'application/octet-stream'},
				body,
				keepalive: onHide && body.size < 60000,
			});
		} catch {
			// Best effort: a lost chunk leaves a gap, the replay still plays
		}
	}

	// Chunk sequence shared by every page load in the session
	private nextSeq(sessionId: string): number {
		const ls = storage();
		const key = `${SEQ_KEY}:${sessionId}`;
		const next = Number(ls?.getItem(key) || '0');
		ls?.setItem(key, String(next + 1));
		return next;
	}

	private acquireLock(): boolean {
		const ls = storage();
		if (!ls || !this.target) {
			return false;
		}
		try {
			const held = JSON.parse(ls.getItem(LOCK_KEY) || 'null') as {tab: string, sid: string, at: number} | null;
			if (held && held.tab !== this.tabId && held.sid === this.target.sessionId &&
				Date.now() - held.at < LOCK_STALE_MS) {
				return false;
			}
		} catch {
			// corrupt lock: take it over
		}
		this.heartbeat();
		return true;
	}

	private heartbeat() {
		const ls = storage();
		if (ls && this.target) {
			ls.setItem(LOCK_KEY, JSON.stringify({tab: this.tabId, sid: this.target.sessionId, at: Date.now()}));
		}
	}

	private releaseLock() {
		const ls = storage();
		try {
			const held = JSON.parse(ls?.getItem(LOCK_KEY) || 'null') as {tab: string} | null;
			if (held?.tab === this.tabId) {
				ls?.removeItem(LOCK_KEY);
			}
		} catch {
			ls?.removeItem(LOCK_KEY);
		}
	}
}
